// src/utils/dashboardApi.js
import { useAuth } from "../contexts/AuthContext";
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const DASHBOARD_API_BASE_URL = "/api/dashboard";
const LIKES_API_BASE_URL = "/api";

const useDashboardApi = () => {
  const { currentUser, userObjectId, logout } = useAuth();
  const [dashloading, setLoading] = useState(false);
  const [dasherror, setError] = useState(null);
  const navigate = useNavigate();

  const getAuthHeader = useCallback(async () => {
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        console.log("token: ", token);
        console.log("userObjectId", userObjectId);
        return { Authorization: `Bearer ${token}` };
      } catch (tokenError) {
        console.error("Error getting ID token:", tokenError);
        return {};
      }
    }
    return {};
  }, [currentUser, userObjectId]);

  const request = useCallback(
    async (
      url,
      endpoint,
      method = "GET",
      data = null,
      customHeaders = {},
      params = null
    ) => {
      setLoading(true);
      setError(null);
      const authHeader = await getAuthHeader();
      const headers = {
        "Content-Type": "application/json",
        ...authHeader,
        ...customHeaders,
      };

      const axiosConfig = {
        method,
        url: `${url}/${endpoint}`,
        headers,
      };

      // Handle different request methods
      if (method === "GET") {
        if (params) {
          axiosConfig.params = params; // Use 'params' for GET query parameters
        }
      } else {
        // For POST/PUT/PATCH requests
        if (data) {
          axiosConfig.data = data; // Request body
        }
        if (params) {
          axiosConfig.params = params; // Query parameters
        }
      }

      console.log(
        `useDashboardApi - Request: ${method} ${url}/${endpoint}`,
        axiosConfig
      );

      try {
        const response = await axios(axiosConfig);
        setLoading(false);
        setError(null);
        console.log("handleResponse - Parsed jsonData:", response.data);
        return response.data;
      } catch (err) {
        setLoading(false);
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        console.error(
          `useDashboardApi - Error during request to ${url}/${endpoint}:`,
          err.response || err
        );

        if (err.response && err.response.status === 401) {
          console.log("Unauthorized request, logging out.");
          logout();
          navigate("/login");
        }
        throw err;
      }
    },
    [getAuthHeader, logout, navigate]
  );

  const dashboardRequest = useCallback(
    (endpoint, method = "GET", data = null, params = null) =>
      request(DASHBOARD_API_BASE_URL, endpoint, method, data, {}, params),
    [request]
  );

  const likesRequest = useCallback(
    (endpoint, method = "GET", data = null, params = null) =>
      request(LIKES_API_BASE_URL, endpoint, method, data, {}, params),
    [request]
  );

  const getDashboard = useCallback(
    (diverId) => dashboardRequest(`diver/${diverId}`),
    [dashboardRequest]
  );

  const updateDashboardSlots = useCallback(
    (diverId, slots) =>
      dashboardRequest(`diver/${diverId}/slots`, "PUT", { slots }),
    [dashboardRequest]
  );

  const createDashboard = useCallback(
    (diverId) => dashboardRequest("/", "POST", { diverId }),
    [dashboardRequest]
  );

  const removeDashboardSlot = useCallback(
    async (diverId, slotIndex) => {
      try {
        const dashboard = await getDashboard(diverId);
        if (!dashboard || !dashboard.slots) {
          throw new Error("Dashboard not found or has no slots");
        }
        const updatedSlotIds = dashboard.slots.map((slot, index) =>
          index === slotIndex ? null : slot?._id
        );
        return await dashboardRequest(`diver/${diverId}/slots`, "PUT", {
          slots: updatedSlotIds,
        });
      } catch (error) {
        console.error("Error removing dashboard slot:", error);
        throw error;
      }
    },
    [getDashboard, dashboardRequest]
  );

  const addDashboardSlot = useCallback(
    (diverId, slots) =>
      dashboardRequest(`diver/${diverId}/slots`, "PUT", { slots }),
    [dashboardRequest]
  );

  return {
    getDashboard,
    updateDashboardSlots,
    createDashboard,
    removeDashboardSlot,
    addDashboardSlot,
    request,
    // Add aliases for component compatibility
    removeDivesiteFromDashboard: removeDashboardSlot,
    addDivesiteToDashboard: addDashboardSlot,
    dashloading,
    dasherror,
    setError,
    likesRequest,
  };
};

export default useDashboardApi;
